import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {
    updateConversationWithSystemMessage,
    useGuideAudioController,
    useGuidePreferences,
} from "@repo/guide-core";
import {GuideStateApi} from "@repo/guide-state";
import type {ReadingStyle as ReadingStyleType} from "@repo/guide-types";

// eslint-disable-next-line static-service/require-shared-package-imports-only -- Should be copied over or moved to a shared package.
import {
    preferredGuideLanguageChangeMessage,
    readingStyleChangeMessage,
} from "~/guide/ui/strings.ts";

import SuccessToast from "../success-toast.tsx";

import GuideLanguageSetting from "./guide-language-setting.tsx";
import GuideVoiceSetting from "./guide-voice-setting.tsx";
import ReadingStyle from "./reading-style.tsx";

interface Props {
    renderGuideLanguages?: boolean;
}

const KhanmigoSettingsForm = ({renderGuideLanguages = false}: Props) => {
    const {
        preferences,
        setPreferences,
        isLoading: isPreferencesLoading,
    } = useGuidePreferences();
    const {selectedVoice} = useGuideAudioController();
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [currentVoice, setCurrentVoice] = useState<string | null>(null);
    const [currentReadingStyle, setCurrentReadingStyle] =
        useState<ReadingStyleType | null>(null);
    const [currentLanguage, setCurrentLanguage] = useState<string | null>(null);

    // Update values when preferences and selectedVoice are received from hooks
    useEffect(() => {
        setCurrentVoice(selectedVoice);
        setCurrentReadingStyle(preferences.readingLevel);
        setCurrentLanguage(preferences.guideLanguageCode);
    }, [
        selectedVoice,
        preferences.readingLevel,
        preferences.guideLanguageCode,
    ]);

    const hasUnsavedChanges =
        currentVoice !== selectedVoice ||
        currentReadingStyle !== preferences.readingLevel ||
        (renderGuideLanguages &&
            currentLanguage !== preferences.guideLanguageCode);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            if (
                renderGuideLanguages &&
                currentLanguage !== preferences.guideLanguageCode
            ) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "UserSettingsAiGuideLanguageChanged",
                    eventSchemaVersion: 1,
                    aiGuidePreviousLanguage: preferences.guideLanguageCode,
                    aiGuidePreviousIsDefault:
                        preferences.guideLanguageCode === "default",
                    aiGuideNewLanguage: currentLanguage || "default",
                    aiGuideNewIsDefault: currentLanguage === "default",
                });
            }

            if (currentVoice && currentVoice !== selectedVoice) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideVoiceSelected",
                    eventSchemaVersion: 1,
                    aiGuideSelectedVoice: currentVoice,
                });
            }
            // Update new reading style info message into conversation.
            if (
                currentReadingStyle &&
                currentReadingStyle !== preferences.readingLevel
            ) {
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationWithSystemMessage(
                        conversation,
                        readingStyleChangeMessage(currentReadingStyle),
                    ),
                );
            }
            // Update new guide language info message into conversation.
            if (
                currentLanguage &&
                currentLanguage !== preferences.guideLanguageCode
            ) {
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationWithSystemMessage(
                        conversation,
                        preferredGuideLanguageChangeMessage(currentLanguage),
                    ),
                );
            }

            await setPreferences({
                guideVoiceDescriptor: currentVoice || null,
                readingLevel: currentReadingStyle,
                guideLanguageCode: renderGuideLanguages
                    ? currentLanguage || "default"
                    : preferences.guideLanguageCode,
            });
            setShowSuccessToast(true);
        } finally {
            setIsSaving(false);
        }
    }, [
        setPreferences,
        currentVoice,
        currentReadingStyle,
        currentLanguage,
        selectedVoice,
        preferences.readingLevel,
        preferences.guideLanguageCode,
        renderGuideLanguages,
    ]);

    return (
        <View style={{gap: sizing.size_320}}>
            <View style={{gap: sizing.size_320}}>
                <ReadingStyle
                    currentReadingStyle={currentReadingStyle}
                    onChange={setCurrentReadingStyle}
                    isLoading={isPreferencesLoading}
                />
                {renderGuideLanguages && (
                    <GuideLanguageSetting
                        currentLanguage={currentLanguage}
                        onChange={setCurrentLanguage}
                        isLoading={isPreferencesLoading}
                    />
                )}
                <GuideVoiceSetting
                    currentVoice={currentVoice}
                    onChange={setCurrentVoice}
                    isLoading={isPreferencesLoading}
                />
            </View>
            <View style={styles.buttonContainer}>
                <Button
                    onClick={handleSave}
                    style={styles.button}
                    spinner={isSaving}
                    disabled={
                        // It's enough to just check for preferencesLoading
                        // because we GuideAudioProvider gets it's initial voice
                        // value from preferences as well
                        isPreferencesLoading || isSaving || !hasUnsavedChanges
                    }
                >
                    {t`Save changes`}
                </Button>
            </View>
            {showSuccessToast && (
                <SuccessToast onClose={() => setShowSuccessToast(false)} />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    buttonContainer: {
        flexDirection: "row",
    },
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
});

export default React.memo(KhanmigoSettingsForm);
